/**
 * Copyright (c) 2026, CheckPlus Limited
 */

/**
 * Error message utilities for handling API and Stripe errors
 * with bilingual support (English and Chinese).
 *
 * This file contains Stripe error code mappings to user-friendly messages.
 */
export const stripeErrorMessages = {
  // Bank account errors
  invalid_bank_account_number: {
    en: "The bank account number is invalid. Please check and try again.",
    zh: "銀行帳戶號碼無效，請檢查後再試。",
  },
  invalid_routing_number: {
    en: "The routing number is invalid. Please check the bank and branch code.",
    zh: "銀行代碼無效，請檢查銀行及分行代碼。",
  },
  bank_account_unusable: {
    en: "This bank account cannot be used. Please try a different account.",
    zh: "此銀行帳戶無法使用，請嘗試其他帳戶。",
  },
  bank_account_verification_failed: {
    en: "Bank account verification failed. Please check your details.",
    zh: "銀行帳戶驗證失敗，請檢查您的資料。",
  },
  // Card errors (for future use)
  card_declined: {
    en: "Your card was declined. Please try a different card.",
    zh: "您的卡片被拒絕，請嘗試其他卡片。",
  },
  expired_card: {
    en: "Your card has expired. Please use a different card.",
    zh: "您的卡片已過期，請使用其他卡片。",
  },
  incorrect_cvc: {
    en: "The security code is incorrect. Please check and try again.",
    zh: "安全碼不正確，請檢查後再試。",
  },
  // Generic errors
  processing_error: {
    en: "An error occurred while processing. Please try again.",
    zh: "處理時發生錯誤，請稍後再試。",
  },
  rate_limit: {
    en: "Too many requests. Please wait a moment and try again.",
    zh: "請求次數過多，請稍後再試。",
  },
};

/**
 * Get a user-friendly error message from a Stripe error
 *
 * @param {Object} error - Stripe error object with code and/or message
 * @param {string} language - Language code ('en' or 'zh')
 * @returns {string} User-friendly error message
 */
export function getStripeErrorMessage(error, language = "en") {
  if (!error) {
    return getDefaultErrorMessage(language);
  }

  // Try to get mapped message by error code
  if (error.code && stripeErrorMessages[error.code]) {
    return stripeErrorMessages[error.code][language] || error.message;
  }

  // Try to get mapped message by error type
  if (error.type && stripeErrorMessages[error.type]) {
    return stripeErrorMessages[error.type][language] || error.message;
  }

  // Use Stripe's error message if available (usually quite descriptive)
  if (error.message) {
    return error.message;
  }

  return getDefaultErrorMessage(language);
}

/**
 * Get default error message
 *
 * @param {string} language - Language code ('en' or 'zh')
 * @returns {string} Default error message
 */
export function getDefaultErrorMessage(language = "en") {
  return language === "zh"
    ? "發生錯誤，請稍後再試。"
    : "An error occurred. Please try again.";
}

/**
 * Get error message from API response
 *
 * @param {Object} response - API response object
 * @param {string} language - Language code ('en' or 'zh')
 * @returns {string} Error message from response or default message
 */
export function getApiErrorMessage(response, language = "en") {
  if (response?.body?.message) {
    return response.body.message;
  }

  if (response?.message) {
    return response.message;
  }

  return getDefaultErrorMessage(language);
}

/**
 * Network/connection error messages
 */
export const networkErrorMessages = {
  en: "Network error. Please check your connection and try again.",
  zh: "網絡錯誤，請檢查您的連線並重試。",
};

/**
 * Get network error message
 *
 * @param {string} language - Language code ('en' or 'zh')
 * @returns {string} Network error message
 */
export function getNetworkErrorMessage(language = "en") {
  return networkErrorMessages[language] || networkErrorMessages.en;
}
